import LaunchIcon from "@mui/icons-material/Launch";
import { Button, colors, Grid, Stack, Typography } from "@mui/material";
import { makeStyles } from "@mui/styles";
import { DocumentModal } from "components/DocumentModal";
import { FileSources } from "constants/sockets";
import formatDistanceToNow from "date-fns/format";
import { useWindowTilesContext } from "desktopWindow/context/WindowTilesContext";
import { getImageSize, isAudio } from "fileBrowser/helpers/fileBrowserHelpers";
import React from "react";
import { ReactComponent as FileIcon } from "../../components/doc_icon.svg";
import { useUserColorSettingsByUsername } from "../../context/GameContext";
import { ChatAttachment, ChatMessage } from "../types";
import { Extension } from "@mui/icons-material";

const useMessageStyles = makeStyles({
	root: {
		padding: "5px 24px",
		"&:hover": {
			background: colors.grey["200"],
		},
		display: "flex",
		flexDirection: "column",
	},
	header: {
		display: "inline-block",
	},
	username: {
		fontWeight: "bold",
		marginRight: 4,
	},
	time: {
		fontWeight: 200,
		// 10px in rem
		fontSize: "0.625rem",
	},
	text: {
		wordWrap: "break-word",
		whiteSpace: "pre-line",
		display: "inline-block",
		flex: 1,
		"& p": {
			margin: 0,
		},
		fontSize: "0.875rem",
	},
});
export const Message = (props: { message: ChatMessage }) => {
	const classes = useMessageStyles();
	const { message } = props;
	const { openWindow } = useWindowTilesContext();
	const [protectedFilePayload, setProtectedFilePayload] = React.useState<any>();
	const onClickAttachment = async ({ _id, name, link, fileUrl, protection, puzzleGamePlugin }: ChatAttachment) => {
		if (protection?.locked) {
			// File is protected (Locked). Need to show a modal to ask the user to unlock it first
			setProtectedFilePayload({
				fileSource: FileSources.CHAT_ATTACHMENT,
				payload: {
					messageId: message._id,
					attachmentId: _id,
				},
			});
		} else if (puzzleGamePlugin?.pluginEmbedUrl) {
			// Handles Embedded puzzle
			const windowHeight = window.innerHeight * 0.85,
				windowWidth = window.innerWidth * 0.75;
			openWindow({
				id: puzzleGamePlugin.name,
				componentName: "EmbeddedPuzzle",
				componentProps: { puzzleGamePlugin },
				windowTitle: puzzleGamePlugin.name,
				size: { height: windowHeight, width: windowWidth },
				minSize: { height: windowHeight * 0.5, width: windowWidth * 0.5 },
			});
		} else if (!!link || !!fileUrl) {
			const file: string = (link || fileUrl)!;
			const isImage = file.includes("jpg") || file.includes("png");
			const isAudioExt: boolean = isAudio(file);

			const defaultWindowWidth = isAudioExt ? 690 : 400;
			const defaultWindowHeight = isAudioExt ? 260 : 400;
			const { width, height } = isImage
				? await getImageSize(file).then((value) => {
						return {
							width: Math.min(800, value.width),
							height: Math.min(600, value.height),
						};
				  })
				: { width: defaultWindowWidth, height: defaultWindowHeight };

			openWindow({
				id: file,
				componentName: "FileViewer",
				componentProps: { file: { id: file, fileUrl: file, name: name } },
				windowTitle: name,
				size: { height, width },
				minSize: { height: height * 0.5, width: width * 0.5 },
			});
		}
	};
	const color = useUserColorSettingsByUsername(message.from);
	return (
		<>
			<div className={classes.root}>
				<Stack>
					<Stack flexDirection={"column"}>
						<span className={classes.time}>
							{formatDistanceToNow(new Date(message.timestamp), "hh:mm aa")}
						</span>
						<span className={classes.username} style={{ color }}>
							{message.from}
						</span>
					</Stack>
					<Typography
						className={classes.text}
						component="span"
						dangerouslySetInnerHTML={{ __html: message.text! }}
					/>
					{!!message.attachments?.length ? (
						<Grid
							item
							sm={12}
							md={12}
							lg={12}
							container
							direction="row"
							alignItems="center"
							justifyContent="flex-end"
							className="attachment-container"
						>
							{message.attachments?.map((attachment) => {
								const { name, link, fileUrl } = attachment;
								const file = link || fileUrl;
								const isImage: boolean = file ? file.includes("jpg") || file.includes("png") : false;
								return (
									<Grid key={name} item sm={12} md={12} style={{ padding: "10px 0px" }}>
										<Button
											variant="outlined"
											onClick={() => {
												onClickAttachment(attachment);
											}}
											style={{
												textTransform: "none",
												backgroundColor: "#FAFAFA",
												borderColor: "#FAFAFA",
												fontSize: 16,
												maxWidth: "100%",
											}}
											startIcon={
												isImage ? (
													<ImageFileThumbnail src={file!} />
												) : attachment.puzzleGamePlugin?.pluginEmbedUrl ? (
													<Extension />
												) : (
													<FileIcon style={{ maxHeight: 25, maxWidth: 25 }} />
												)
											}
											title={name}
											endIcon={<LaunchIcon />}
										>
											<span
												style={{
													width: "calc(100% - 50px)",
													whiteSpace: "normal",
													overflow: "hidden",
													textOverflow: "ellipsis",
												}}
											>
												{name ?? attachment.puzzleGamePlugin?.name}
											</span>
										</Button>
									</Grid>
								);
							})}
						</Grid>
					) : (
						<></>
					)}
				</Stack>
			</div>
			<DocumentModal
				isOpen={!!protectedFilePayload}
				onClose={() => setProtectedFilePayload(null)}
				data={protectedFilePayload}
			/>
		</>
	);
};

const ImageFileThumbnail = (props: { src: string }) => {
	return <img style={{ width: "100%", maxHeight: 25, maxWidth: 25 }} src={props.src} alt={props.src} />;
};
